import {
  Action,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { ExpandableDataTable } from "@/modules/shared/ui/components/custom/ExpandableDataTable"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { useEffect, useState, useMemo, useCallback } from "react"
import { ApplicationListItem } from "../../dtos/ApplicantListItem"
import { ApplicationDetails } from "../../dtos/ApplicationDetails"
import { useNavigate, useParams, useLocation, Link } from "react-router-dom"
import { DeleteConfirmationDialog } from "@/modules/recruitment/vacancies/ui/components/DeleteConfirmationDialog"
import { Badge } from "@/modules/shared/ui/components/badge"
import { useApplicants } from "../hooks/useApplicants"
import { useRescoreApplication } from "@/modules/recruitment/hooks/useApplications"
import { useAiScreeningCall } from "../hooks/useAiScreeningCall"
import { SearchFilterBar } from "@/modules/shared/ui/components/custom/SearchFilterBar"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { toast } from "sonner"
import { useApplicationSignalR } from "../../hooks/useApplicationSignalR"
import { UploadCvDialog } from "../components/UploadCvDialog"
import { AcceptCandidateDialog } from "../components/AcceptCandidateDialog"
import { RejectCandidateDialog } from "../components/RejectCandidateDialog"
import { BulkRejectDialog } from "../components/BulkRejectDialog"
import {
  BulkCreateVideoInterviewDialog,
  BulkCreateVideoInterviewData,
} from "../components/BulkCreateVideoInterviewDialog"
import { BulkAiCallDialog } from "../components/BulkAiCallDialog"
import { AiCallQuotaMeter } from "../components/AiCallQuotaMeter"
import { useCallQuota } from "../hooks/useCallQuota"
import {
  GetApplicationsForVacancy,
  rejectCandidate,
  hireCandidate,
  bulkRejectCandidates,
  BulkRejectionProgressDto,
  bulkCreateVideoInterviews,
  BulkVideoProgressDto,
  bulkCreateAiCalls,
  BulkAiCallProgressDto,
  BulkAiCallCandidateProgressDto,
} from "../../applicants-service"
import { useCvUpload } from "@/modules/recruitment/hooks/useCvUpload"
import { vacancyService } from "@/modules/recruitment/infrastructure/vacancy-service"
import { VacancyStatus } from "@/modules/recruitment/enums/VacancyStatus"
import { Button } from "@/modules/shared/ui/components/button"
import {
  ArrowLeft,
  Upload,
  Check,
  X,
  Bot,
  Users,
  Fingerprint,
  Video,
  Info,
  UserMinus,
  ChevronDown,
  Loader2,
  Sparkles,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/modules/shared/ui/components/dropdown-menu"
import { EmployeeFormValues } from "@/modules/onboarding/ui/components/EmployeeForm"
import {
  getApplicationStageLabel,
  getApplicationStageBadgeVariant,
  ApplicationStage,
} from "../../enums/CandidateStatus"
import { parseApplicationStage } from "../../dtos/ApplicationDetails"
import { InterviewStatus } from "@/modules/recruitment/applications/enums/InterviewStatus"
import { InterviewType } from "@/modules/recruitment/applications/enums/InterviewType"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { CreateExpertInterviewDialog } from "../components/CreateExpertInterviewDialog"
import { InterviewQuestion } from "@/modules/public/dto/videoInterviewDto"
import { CreateVideoInterviewDialog } from "../components/CreateVideoInterviewDialog"
import { SendRegretDialog } from "@/modules/recruitment/vacancies/ui/components/SendRegretDialog"
import axios from "axios"
import { DebugAiCallDialog } from "../components/DebugAiCallDialog"
import { useUserProfile } from "@/modules/profile/hooks/useProfile"
import { IdVerificationDialog } from "../components/IdVerificationDialog"
import { useBulkRejectionContext } from "@/modules/recruitment/context/BulkRejectionContext"
import { useBulkVideoContext } from "@/modules/recruitment/context/BulkVideoContext"
import { useBulkAiCallContext } from "@/modules/recruitment/context/BulkAiCallContext"

export const ApplicantsPage = () => {
  type StageFilter = ApplicationStage | "All"
  const stageFilterOptions: { label: string; value: string }[] = [
    { label: "All", value: "All" },
    { label: "Applied", value: String(ApplicationStage.Applied) },
    { label: "Screened", value: String(ApplicationStage.Screened) },
    { label: "Candidate", value: String(ApplicationStage.Candidate) },
    { label: "Hired", value: String(ApplicationStage.Hired) },
    { label: "Rejected", value: String(ApplicationStage.Rejected) },
  ]

  const [searchTerm, setSearchTerm] = useState("")
  const [stageFilter, setStageFilter] = useState<StageFilter>("All")
  const navigate = useNavigate()
  const location = useLocation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedApplicant, setSelectedApplicant] = useState<
    ApplicationListItem | undefined
  >(undefined)
  const [isDeleting] = useState(false)
  const { vacancyId } = useParams<{ vacancyId: string }>()
  const { scheduleExpertInterview, createVideoInterview } = useApplicants()
  const { startAiScreeningCall } = useAiScreeningCall()
  const { currentOrganization } = useOrganizationsService()
  const { data: userProfile } = useUserProfile()
  const rescoreMutation = useRescoreApplication()

  const isApplicationScored = (app: { scoringStatus?: string }) =>
    (app.scoringStatus ?? "Scored") === "Scored"

  const isRescoringApplication = (applicationId: string) =>
    rescoreMutation.isPending &&
    rescoreMutation.variables?.applicationId === applicationId

  const handleRescoreFromList = (application: ApplicationDetails) => {
    if (!currentOrganization?.id) return
    rescoreMutation.mutate(
      {
        organizationId: currentOrganization.id,
        applicationId: application.id,
      },
      {
        onSuccess: () => {
          refetchApplications()
        },
      },
    )
  }
  const [_, setApplicants] = useState<ApplicationListItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const vacancyTitle = location.state?.vacancyTitle as string | undefined
  const vacancyBaseScore = location.state?.baseScore ?? (0 as number)
  const [vacancyStatus, setVacancyStatus] = useState<VacancyStatus | null>(null)
  const isVacancyClosed =
    vacancyStatus === VacancyStatus.Closed ||
    vacancyStatus === VacancyStatus.Expired
  const [isUploadCvDialogOpen, setIsUploadCvDialogOpen] = useState(false)
  const { upload: uploadCvFile } = useCvUpload({
    organizationId: currentOrganization?.id ?? "",
    vacancyId: vacancyId ?? "",
    onCompleted: async () => {
      if (vacancyId && currentOrganization?.id) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }
    },
  })

  const [applicationDetails, setApplicationDetails] = useState<
    ApplicationDetails[]
  >([])
  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationListItem | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isExpertInterviewDialogOpen, setIsExpertInterviewDialogOpen] =
    useState(false)
  const [isSchedulingExpert, setIsSchedulingExpert] = useState(false)
  const [expertInterviewApplication, setExpertInterviewApplication] =
    useState<ApplicationDetails | null>(null)
  const [isVideoInterviewDialogOpen, setIsVideoInterviewDialogOpen] =
    useState(false)
  const [videoInterviewApplication, setVideoInterviewApplication] =
    useState<ApplicationDetails | null>(null)
  const [isCreatingVideoInterview, setIsCreatingVideoInterview] =
    useState(false)
  const [showSendRegretDialog, setShowSendRegretDialog] = useState(false)
  const [hiredCandidateName, setHiredCandidateName] = useState<string>("")
  const [isPhoneRequiredDialogOpen, setIsPhoneRequiredDialogOpen] =
    useState(false)
  const [isIdVerificationDialogOpen, setIsIdVerificationDialogOpen] =
    useState(false)
  const [idVerificationApplication, setIdVerificationApplication] =
    useState<ApplicationDetails | null>(null)

  // ── Bulk selection state ────────────────────────────────────────────────
  type BulkAction = "reject" | "video" | "ai-call"
  const BULK_AI_CALL_LIMIT = 10
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isBulkRejectDialogOpen, setIsBulkRejectDialogOpen] = useState(false)
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false)
  const [isBulkVideoDialogOpen, setIsBulkVideoDialogOpen] = useState(false)
  const [isBulkVideoSubmitting, setIsBulkVideoSubmitting] = useState(false)
  const [isBulkAiCallDialogOpen, setIsBulkAiCallDialogOpen] = useState(false)
  const [isBulkAiCallSubmitting, setIsBulkAiCallSubmitting] = useState(false)
  const { addJob, updateJob } = useBulkRejectionContext()
  const { addJob: addVideoJob, updateJob: updateVideoJob } =
    useBulkVideoContext()
  const {
    addJob: addAiCallJob,
    updateJob: updateAiCallJob,
    updateCandidate: updateAiCallCandidate,
  } = useBulkAiCallContext()
  const { data: callQuota, refetch: refetchCallQuota } = useCallQuota()

  const startBulkAction = (action: BulkAction) => {
    setBulkAction(action)
    setIsSelectionMode(true)
  }

  const toggleSelection = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          // The bulk-AI-call batch is capped at the quota window limit —
          // further selections are ignored once the cap is reached.
          if (bulkAction === "ai-call" && next.size >= BULK_AI_CALL_LIMIT) {
            return prev
          }
          next.add(id)
        }
        return next
      })
    },
    [bulkAction],
  )

  const exitSelectionMode = () => {
    setIsSelectionMode(false)
    setBulkAction(null)
    setSelectedIds(new Set())
  }

  const refetchApplications = useCallback(async () => {
    if (!vacancyId || !currentOrganization?.id) return
    const data = await GetApplicationsForVacancy(
      currentOrganization.id,
      vacancyId,
    )
    setApplicationDetails(data)
  }, [vacancyId, currentOrganization?.id])

  useEffect(() => {
    if (!vacancyId || !currentOrganization?.id) return
    vacancyService
      .getVacancyById(currentOrganization.id, vacancyId)
      .then((v) => setVacancyStatus(v.status))
      .catch(() => setVacancyStatus(null))
  }, [vacancyId, currentOrganization?.id])

  const handleBulkRejectionProgress = useCallback(
    (progress: BulkRejectionProgressDto) => {
      updateJob(progress)
    },
    [updateJob],
  )

  const handleBulkVideoProgress = useCallback(
    (progress: BulkVideoProgressDto) => {
      updateVideoJob(progress)
    },
    [updateVideoJob],
  )

  const handleBulkAiCallProgress = useCallback(
    (progress: BulkAiCallProgressDto) => {
      updateAiCallJob(progress)
    },
    [updateAiCallJob],
  )

  const handleBulkAiCallCandidateProgress = useCallback(
    (progress: BulkAiCallCandidateProgressDto) => {
      updateAiCallCandidate(progress)
    },
    [updateAiCallCandidate],
  )

  const handleBulkAiCallComplete = useCallback(() => {
    refetchApplications()
    refetchCallQuota()
  }, [refetchApplications, refetchCallQuota])

  const applicationHasVideoInterview = (application: ApplicationDetails) =>
    application.interviews?.some(
      (i) => i.type === InterviewType[InterviewType.VideoScreening],
    ) ?? false

  const getDetailedActions = (
    application: ApplicationDetails,
  ): Action<ApplicationDetails>[] => {
    if (!isApplicationScored(application)) {
      // Unscored applications are locked down to a single Rescore action —
      // scoring must complete before an admin can act on the candidate.
      const isThisRowRescoring = isRescoringApplication(application.id)
      return [
        {
          label: isThisRowRescoring ? "Rescoring…" : "Rescore",
          onClick: (app) => handleRescoreFromList(app),
          disabled: () => rescoreMutation.isPending,
        },
      ]
    }
    const baseActions: Action<ApplicationDetails>[] = [
      {
        label: "View Applicant",
        onClick: (app) => {
          navigate(`/recruitment/applications/${app.id}`)
        },
      },
    ]
    const stage = parseApplicationStage(application.currentStage)

    if (
      stage !== ApplicationStage.Hired &&
      stage !== ApplicationStage.Rejected
    ) {
      baseActions.push(
        {
          label: "Create AI Screening",
          onClick: (app) => handleAiScreenCall(app),
        },
        {
          label: "Schedule Expert Interview",
          onClick: (app) => {
            setExpertInterviewApplication(app)
            setIsExpertInterviewDialogOpen(true)
          },
        },
      )

      if (!applicationHasVideoInterview(application)) {
        baseActions.push({
          label: "Create Video Interview",
          onClick: (app) => {
            setVideoInterviewApplication(app)
            setIsVideoInterviewDialogOpen(true)
          },
        })
      }

      baseActions.push({
        label: "Run ID Verification",
        onClick: (app) => {
          setIdVerificationApplication(app)
          setIsIdVerificationDialogOpen(true)
        },
      })
    }

    if (
      stage !== ApplicationStage.Hired &&
      stage !== ApplicationStage.Rejected
    ) {
      baseActions.push(
        {
          label: "Accept Candidate",
          onClick: (app) => {
            setSelectedApplication(app)
            setIsAcceptDialogOpen(true)
          },
        },
        {
          label: "Reject Candidate",
          onClick: (app) => {
            setSelectedApplication(app)
            setIsRejectDialogOpen(true)
          },
        },
      )
    }
    return baseActions
  }

  const handleSendRegretMessages = async () => {
    if (!vacancyId || !currentOrganization?.id) return
    try {
      const result = await vacancyService.notifyClosedVacancyCandidates(
        currentOrganization.id,
        vacancyId,
      )
      if (result.isSuccess && result.value !== undefined) {
        toast.success(`Regret messages sent to ${result.value} candidates`)
      } else {
        toast.error(result.error ?? "Failed to send regret messages")
      }
      setShowSendRegretDialog(false)
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to send regret messages")
    }
  }

  const handleNewApplication = (newApplication: ApplicationListItem) => {
    setApplicants((prev) => {
      if (prev.some((app) => app.id === newApplication.id)) return prev
      return [newApplication, ...prev]
    })
    toast.success(
      `New application from ${newApplication.candidate.firstName} ${newApplication.candidate.lastName}` +
        (newApplication.matchScore
          ? ` (${newApplication.matchScore?.score ?? 0}% match)`
          : ""),
      { duration: 5000 },
    )
  }

  useApplicationSignalR(
    currentOrganization?.id,
    vacancyId,
    handleNewApplication,
    (updatedApplication) => {
      setApplicationDetails((prev) =>
        prev.map((app) =>
          app.id === updatedApplication.id
            ? ({
                ...app,
                ...updatedApplication,
                // SignalR notifications use ApplicationDto which doesn't include
                // isIdVerificationCompleted — preserve the value already in state
                // so a call webhook update doesn't silently clear the green dot.
                isIdVerificationCompleted: app.isIdVerificationCompleted,
              } as ApplicationDetails)
            : app,
        ),
      )
      setApplicants((prev) =>
        prev.map((app) =>
          app.id === updatedApplication.id
            ? { ...app, ...updatedApplication }
            : app,
        ),
      )
    },
    undefined,
    handleBulkRejectionProgress,
    refetchApplications,
    handleBulkVideoProgress,
    refetchApplications,
    handleBulkAiCallProgress,
    handleBulkAiCallComplete,
    handleBulkAiCallCandidateProgress,
  )

  useEffect(() => {
    const fetchApplicants = async () => {
      if (!vacancyId || !currentOrganization?.id) return
      setIsLoading(true)
      try {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      } catch (error) {
        console.error("Error fetching applicants:", error)
        toast.error("Failed to load applicants")
      } finally {
        setIsLoading(false)
      }
    }
    fetchApplicants()
  }, [vacancyId, currentOrganization?.id])

  const handleDeleteApplicant = () => {
    if (!selectedApplicant) return
  }

  const handleUploadCv = (files: File[]) => {
    setIsUploadCvDialogOpen(false)
    uploadCvFile(files)
  }

  const handleVideoInterviewCreate = async (data: {
    questions: Partial<InterviewQuestion>[]
  }) => {
    if (!videoInterviewApplication) return
    setIsCreatingVideoInterview(true)
    try {
      await createVideoInterview({
        applicationId: videoInterviewApplication.id,
        questions: data.questions,
        maxVideoAttempts: 3,
        videoDeadline: undefined,
      })
      toast.success("Video interview invite sent successfully")
      setIsVideoInterviewDialogOpen(false)
      setVideoInterviewApplication(null)
      if (vacancyId && currentOrganization?.id) {
        const updated = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(updated)
      }
    } catch (error) {
      console.error(error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string } | undefined)?.error
        : undefined
      toast.error(message || "Failed to create video interview")
    } finally {
      setIsCreatingVideoInterview(false)
    }
  }

  const handleAcceptCandidate = async (
    contractFile: File,
    employeeData: EmployeeFormValues,
  ) => {
    if (!currentOrganization?.id || !selectedApplication) return
    setIsAccepting(true)
    try {
      await hireCandidate(
        selectedApplication.id,
        selectedApplication.candidate.id,
        contractFile,
        employeeData.jobStartDate,
        employeeData.workflowTemplateIds,
        employeeData.documentsNeeded,
      )
      const candidateName = `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
      setHiredCandidateName(candidateName)
      toast.success(`${candidateName} accepted successfully`)
      setIsAcceptDialogOpen(false)
      setSelectedApplication(null)
      if (vacancyId) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }

      setShowSendRegretDialog(true)
    } catch (error: unknown) {
      console.error("Error accepting candidate:", error)
      if (axios.isAxiosError(error)) {
        const backendMessage =
          error.response?.data?.error ??
          error.response?.data?.message ??
          "Failed to accept candidate"
        toast.error(backendMessage)
      } else {
        toast.error("Failed to accept candidate")
      }
    } finally {
      setIsAccepting(false)
    }
  }

  const handleExpertInterviewSchedule = async (data: {
    title: string
    notes: string
    timeSlots: { startTime: string; endTime: string }[]
  }) => {
    if (!expertInterviewApplication) return
    setIsSchedulingExpert(true)
    try {
      await scheduleExpertInterview({
        applicationId: expertInterviewApplication.id,
        title: data.title,
        notes: data.notes,
        timeSlots: data.timeSlots,
      })
      toast.success(
        "Expert interview scheduled successfully. Candidate has been notified.",
      )
      setIsExpertInterviewDialogOpen(false)
      setExpertInterviewApplication(null)
      if (vacancyId && currentOrganization?.id) {
        const updated = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(updated)
      }
    } catch (error) {
      console.error("Failed to schedule expert interview", error)
      const message =
        axios.isAxiosError(error) && error.response?.data?.error
          ? error.response.data.error
          : "Failed to schedule expert interview"
      toast.error(message)
    } finally {
      setIsSchedulingExpert(false)
    }
  }

  const handleAiScreenCall = async (app: ApplicationDetails) => {
    await startAiScreeningCall({
      applicationId: app.id,
      phoneNumber: userProfile?.phoneNumber,
      onPhoneRequired: () => setIsPhoneRequiredDialogOpen(true),
      onSuccess: async () => {
        if (vacancyId && currentOrganization?.id) {
          const updated = await GetApplicationsForVacancy(
            currentOrganization.id,
            vacancyId,
          )
          setApplicationDetails(updated)
        }
        refetchCallQuota()
      },
    })
  }

  const handleRejectCandidate = async (reason: string) => {
    if (!currentOrganization?.id || !selectedApplication) return
    setIsRejecting(true)
    try {
      await rejectCandidate(
        currentOrganization.id,
        selectedApplication.id,
        reason,
      )
      toast.success(
        `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName} rejected`,
      )
      setIsRejectDialogOpen(false)
      setSelectedApplication(null)
      if (vacancyId) {
        const data = await GetApplicationsForVacancy(
          currentOrganization.id,
          vacancyId,
        )
        setApplicationDetails(data)
      }
    } catch (error) {
      console.error("Error rejecting candidate:", error)
      toast.error("Failed to reject candidate")
    } finally {
      setIsRejecting(false)
    }
  }

  const handleBulkReject = async (reason: string) => {
    if (!currentOrganization?.id || selectedIds.size === 0) return
    setIsBulkSubmitting(true)
    try {
      const job = await bulkRejectCandidates(
        currentOrganization.id,
        [...selectedIds],
        reason || undefined,
      )
      addJob(job)
      toast.success(
        `Rejection queued for ${job.total} candidate${job.total !== 1 ? "s" : ""}. Progress will appear in notifications.`,
      )
      setIsBulkRejectDialogOpen(false)
      exitSelectionMode()
    } catch (error) {
      console.error("Error queuing bulk rejection:", error)
      toast.error("Failed to queue bulk rejection")
    } finally {
      setIsBulkSubmitting(false)
    }
  }

  const handleBulkVideoInterviewCreate = async (
    data: BulkCreateVideoInterviewData,
  ) => {
    if (!currentOrganization?.id || selectedIds.size === 0) return
    setIsBulkVideoSubmitting(true)
    try {
      const job = await bulkCreateVideoInterviews(
        currentOrganization.id,
        [...selectedIds],
        data.questions,
        data.videoDeadline,
        data.maxVideoAttempts,
      )
      addVideoJob(job)
      toast.success(
        `Video interview invites queued for ${job.total} candidate${job.total !== 1 ? "s" : ""}. Progress will appear in notifications.`,
      )
      setIsBulkVideoDialogOpen(false)
      exitSelectionMode()
    } catch (error) {
      console.error("Error queuing bulk video interviews:", error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string } | undefined)?.error
        : undefined
      toast.error(message || "Failed to queue bulk video interviews")
    } finally {
      setIsBulkVideoSubmitting(false)
    }
  }

  const handleBulkAiCallCreate = async () => {
    if (!currentOrganization?.id || selectedIds.size === 0) return
    setIsBulkAiCallSubmitting(true)
    try {
      const job = await bulkCreateAiCalls(currentOrganization.id, [
        ...selectedIds,
      ])
      addAiCallJob({ ...job, candidates: job.candidates ?? [] })
      toast.success(
        `AI calls queued for ${job.total} candidate${job.total !== 1 ? "s" : ""}. Progress will appear in notifications.`,
      )
      setIsBulkAiCallDialogOpen(false)
      exitSelectionMode()
      refetchCallQuota()
    } catch (error) {
      console.error("Error queuing bulk AI calls:", error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as { error?: string } | undefined)?.error
        : undefined
      toast.error(message || "Failed to queue bulk AI calls")
    } finally {
      setIsBulkAiCallSubmitting(false)
    }
  }

  const filteredApplicationDetails = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    return applicationDetails.filter((app) => {
      const stage = parseApplicationStage(app.currentStage)
      const matchesStage = stageFilter === "All" || stage === stageFilter
      const matchesSearch =
        !term ||
        `${app.candidate.firstName} ${app.candidate.lastName}`
          .toLowerCase()
          .includes(term) ||
        app.candidate.email.toLowerCase().includes(term)
      return matchesStage && matchesSearch
    })
  }, [applicationDetails, stageFilter, searchTerm])

  const BULK_REJECT_LIMIT = 200
  const bulkEligibleApplications =
    bulkAction === "video"
      ? filteredApplicationDetails.filter(
          (a) => !applicationHasVideoInterview(a),
        )
      : filteredApplicationDetails
  const bulkSelectionLimit =
    bulkAction === "ai-call" ? BULK_AI_CALL_LIMIT : BULK_REJECT_LIMIT
  const selectableIds = bulkEligibleApplications
    .map((a) => a.id)
    .slice(0, bulkSelectionLimit)
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id))

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableIds))
    }
  }

  const checkboxColumn: TableColumn<ApplicationDetails> = {
    key: "id",
    label: "",
    headerClassName: "w-10",
    cellClassName: "w-10",
    render: (application) => {
      const isSelected = selectedIds.has(application.id)
      const aiCallCapReached =
        bulkAction === "ai-call" &&
        !isSelected &&
        selectedIds.size >= BULK_AI_CALL_LIMIT
      const disabled =
        (bulkAction === "video" && applicationHasVideoInterview(application)) ||
        aiCallCapReached
      const checkbox = (
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          disabled={disabled}
          className={`h-4 w-4 accent-primary ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
        />
      )
      if (!disabled) return checkbox
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">{checkbox}</span>
            </TooltipTrigger>
            <TooltipContent>
              {aiCallCapReached
                ? `Max ${BULK_AI_CALL_LIMIT} calls per batch`
                : "Video interview already sent"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
  }

  const baseColumns: TableColumn<ApplicationDetails>[] = [
    {
      key: "candidate",
      label: "Name",
      render: (application: ApplicationDetails) => (
        <div className="flex flex-col min-w-0 max-w-md">
          <button
            className="font-medium text-foreground truncate hover:text-primary hover:underline text-left"
            onClick={() =>
              navigate(`/recruitment/applications/${application.id}`)
            }
          >
            {application.candidate.firstName} {application.candidate.lastName}
          </button>
          <span className="text-xs text-muted-foreground">
            {application.candidate.email}
          </span>
        </div>
      ),
    },
    {
      key: "candidateId",
      label: "Status",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const stage = parseApplicationStage(application.currentStage)
        return (
          <div className="flex flex-col min-w-0 max-w-md">
            <div className="text-center">
              <Badge variant={getApplicationStageBadgeVariant(stage)}>
                {getApplicationStageLabel(stage)}
              </Badge>
            </div>
          </div>
        )
      },
    },
    {
      key: "matchScore",
      label: "Match Score",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        if (isRescoringApplication(application.id)) {
          return (
            <div className="text-center">
              <Badge
                variant="outline"
                className="text-[11px] px-2 py-0.5 rounded-full border-blue-400/60 text-blue-800 bg-blue-50 inline-flex items-center gap-1"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Rescoring…
              </Badge>
            </div>
          )
        }
        const status = application.scoringStatus ?? "Scored"
        if (status !== "Scored") {
          const failureLabel =
            application.scoringFailureReason === "InsufficientWalletBalance"
              ? "insufficient credits"
              : status === "Pending"
                ? "in progress"
                : "unscored"
          return (
            <div className="text-center">
              <Badge
                variant="outline"
                className="text-[11px] px-2 py-0.5 rounded-full border-yellow-400/60 text-yellow-800 bg-yellow-50"
              >
                Unscored {failureLabel}
              </Badge>
            </div>
          )
        }
        const isBelowBase = application.matchScore.score < vacancyBaseScore
        return (
          <div className="text-center">
            <div
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isBelowBase ? "text-red-700" : "text-green-700"}`}
            >
              {application.matchScore.score}%
              {isBelowBase && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-red-400 cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[260px] p-0 overflow-hidden"
                  >
                    <div className="px-3.5 py-2.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <Info className="w-3.5 h-3.5 shrink-0 text-red-400" />
                        <span className="text-[13px] font-semibold text-white leading-tight">
                          Below Base Score
                        </span>
                      </div>
                      <p className="text-[11px] items-center leading-relaxed text-muted-foreground">
                        This applicant scored {application.matchScore.score}%,
                        which is below the vacancy rejection score of{" "}
                        {vacancyBaseScore}%.
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: "tailoringRisk" as keyof ApplicationDetails,
      label: "Tailoring Risk",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const score = application.matchScore?.aiProbabilityScore
        if (score === undefined || score <= 0) {
          return (
            <div className="text-center text-muted-foreground text-sm">-</div>
          )
        }
        return (
          <div className="text-center flex justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    className={`cursor-help flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium shadow-sm border-0 ${score >= 0.7 ? "bg-red-100 text-red-700 hover:bg-red-200" : score >= 0.4 ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                  >
                    <Bot className="w-3 h-3" />
                    {score >= 0.7 ? "High" : score >= 0.4 ? "Medium" : "Low"}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Tailoring Risk:{" "}
                    {score >= 0.7 ? "High" : score >= 0.4 ? "Medium" : "Low"} (
                    {(score * 100).toFixed(0)}%). Indicates how closely the CV
                    mirrors the job description requirements.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )
      },
    },
    {
      key: "appliedAt",
      label: "Applied",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => (
        <div className="text-center">
          <span className="text-sm text-muted-foreground">
            {new Date(application.appliedAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      ),
    },
    {
      key: "interviews",
      label: "Screening",
      headerClassName: "text-center",
      render: (application: ApplicationDetails) => {
        const hasCompletedScreening = application.interviews?.some(
          (interview) =>
            interview.type === InterviewType[InterviewType.AiScreening] &&
            interview.status === InterviewStatus[InterviewStatus.Completed],
        )
        const hasCompletedExpert = application.interviews?.some(
          (i) =>
            i.type === InterviewType[InterviewType.ExpertSession] &&
            i.status === InterviewStatus[InterviewStatus.Completed],
        )

        const steps = [
          {
            label: "AI Screening",
            description: "Initial AI-powered screening assessment",
            icon: Bot,
            completed: hasCompletedScreening,
            accentColor: "violet",
          },
          {
            label: "Expert Interview",
            description: "One-on-one interview with domain expert",
            icon: Users,
            completed: hasCompletedExpert,
            accentColor: "blue",
          },
          {
            label: "Video Interview",
            description: "Asynchronous video interview",
            icon: Video,
            completed: application.interviews?.some(
              (i) =>
                i.type === InterviewType[InterviewType.VideoScreening] &&
                i.status === InterviewStatus[InterviewStatus.Completed],
            ),
            accentColor: "purple",
          },
          {
            label: "ID Verification",
            description: "IDAS identity verification check",
            icon: Fingerprint,
            completed: application.isIdVerificationCompleted,
            accentColor: "teal",
          },
        ] as const

        return (
          <TooltipProvider>
            <div className="flex items-center justify-center gap-1">
              <div className="flex items-center gap-1.5">
                {steps.map((step, idx) => {
                  const Icon = step.icon
                  const isComplete = !!step.completed
                  return (
                    <Tooltip key={idx}>
                      <TooltipTrigger asChild>
                        <div
                          className={`group relative w-6 h-6 rounded-full flex items-center justify-center cursor-help transition-all duration-300 ease-out ${
                            isComplete
                              ? "bg-linear-to-br from-green-400 to-emerald-500 shadow-sm shadow-green-200 hover:shadow-md hover:shadow-green-300 hover:scale-110"
                              : "bg-muted border border-border hover:bg-muted hover:border-border hover:scale-110"
                          }`}
                        >
                          {isComplete ? (
                            <Check
                              className="w-3.5 h-3.5 text-white drop-shadow-sm"
                              strokeWidth={2.5}
                            />
                          ) : (
                            <Icon className="w-3 h-3 text-muted-foreground group-hover:text-muted-foreground transition-colors duration-200" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[200px] p-0 overflow-hidden"
                      >
                        <div className="px-3.5 py-2.5 space-y-1">
                          <div className="flex items-center gap-2">
                            <Icon
                              className={`w-3.5 h-3.5 shrink-0 ${
                                isComplete
                                  ? "text-green-400"
                                  : "text-muted-foreground"
                              }`}
                            />
                            <span className="text-[13px] font-semibold text-white leading-tight">
                              {step.label}
                            </span>
                          </div>
                          <p className="text-[11px] leading-relaxed text-muted-foreground">
                            {step.description}
                          </p>
                          <div
                            className={`inline-flex items-center gap-1 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              isComplete
                                ? "bg-green-500/20 text-green-300"
                                : "bg-white/10 text-muted-foreground"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                isComplete
                                  ? "bg-green-400"
                                  : "bg-muted-foreground"
                              }`}
                            />
                            {isComplete ? "Completed" : "Pending"}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          </TooltipProvider>
        )
      },
    },
  ]

  const columns: TableColumn<ApplicationDetails>[] = isSelectionMode
    ? [checkboxColumn, ...baseColumns]
    : baseColumns

  const renderSkillBreakdown = (row: ApplicationDetails) => {
    const { matchScore } = row
    const categoryOrder = [
      "RequiredSkills",
      "OptionalSkills",
      "KeyResponsibilities",
      "Certification",
    ]
    const categoryConfig: Record<
      string,
      { label: string; badgeClass: string; iconColor: string }
    > = {
      RequiredSkills: {
        label: "Required Skills",
        badgeClass: "bg-red-50 text-red-700 border-red-200",
        iconColor: "text-red-500",
      },
      OptionalSkills: {
        label: "Optional Skills",
        badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
        iconColor: "text-blue-500",
      },
      KeyResponsibilities: {
        label: "Key Responsibilities",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
        iconColor: "text-amber-500",
      },
      Certification: {
        label: "Certifications",
        badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
        iconColor: "text-purple-500",
      },
    }

    const grouped = matchScore.skillBreakdown.reduce<
      Record<string, typeof matchScore.skillBreakdown>
    >((acc, skill) => {
      const cat = skill.category || "Other"
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(skill)
      return acc
    }, {})

    const sortedCategories = [
      ...categoryOrder.filter((c) => grouped[c]),
      ...Object.keys(grouped).filter((c) => !categoryOrder.includes(c)),
    ]

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground">
            Skill Breakdown
          </h4>
        </div>

        {sortedCategories.map((category) => {
          const config = categoryConfig[category] || {
            label: category,
            badgeClass: "bg-muted text-foreground border-border",
            iconColor: "text-muted-foreground",
          }
          const skills = grouped[category]

          return (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`text-[11px] px-2 py-0.5 font-semibold border ${config.badgeClass}`}
                >
                  {config.label}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {skills.length} item{skills.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="grid gap-2">
                {skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {skill.skillName}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {skill.explanation}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-muted-foreground">
                            Match
                          </span>
                          <span className="text-xs font-semibold text-foreground">
                            {skill.matchPercentage}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              skill.matchPercentage >= 80
                                ? "bg-green-500"
                                : skill.matchPercentage >= 60
                                  ? "bg-yellow-500"
                                  : "bg-red-400"
                            }`}
                            style={{ width: `${skill.matchPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}

        {(matchScore.strengths.length > 0 ||
          matchScore.weaknesses.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
            <div>
              <h5 className="text-xs font-semibold text-green-700 mb-1.5">
                Strengths
              </h5>
              {matchScore.strengths.length > 0 ? (
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {matchScore.strengths.map((s, i) => (
                    <li
                      key={i}
                      className="text-xs text-muted-foreground flex items-start gap-1.5"
                    >
                      <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                      <span className="wrap-break-words">{s}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No strengths identified
                </p>
              )}
            </div>
            <div>
              <h5 className="text-xs font-semibold text-red-700 mb-1.5">
                Weaknesses
              </h5>
              {matchScore.weaknesses.length > 0 ? (
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {matchScore.weaknesses.map((w, i) => (
                    <li
                      key={i}
                      className="text-xs text-muted-foreground flex items-start gap-1.5"
                    >
                      <X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                      <span className="wrap-break-words">{w}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No weaknesses identified
                </p>
              )}
            </div>
          </div>
        )}

        {/* CV Tailoring Analysis */}
        {matchScore.aiProbabilityScore !== undefined &&
          matchScore.aiProbabilityScore > 0 &&
          matchScore.tailoringSignals && (
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <Bot className="w-4 h-4 text-muted-foreground" />
                  CV Tailoring Analysis
                </h5>
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                    matchScore.aiProbabilityScore >= 0.7
                      ? "bg-red-100 text-red-700"
                      : matchScore.aiProbabilityScore >= 0.4
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                  }`}
                >
                  <Bot className="w-3 h-3" />
                  Tailoring Risk:{" "}
                  {matchScore.aiProbabilityScore >= 0.7
                    ? "High"
                    : matchScore.aiProbabilityScore >= 0.4
                      ? "Medium"
                      : "Low"}
                  <span className="opacity-70">
                    ({(matchScore.aiProbabilityScore * 100).toFixed(0)}%)
                  </span>
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Structural Similarity */}
                {matchScore.tailoringSignals.structuralSimilarity && (
                  <div
                    className={`rounded-md border px-3 py-2 ${
                      matchScore.tailoringSignals.structuralSimilarity.detected
                        ? "border-amber-200 bg-amber-50/40"
                        : "border-green-200 bg-green-50/40"
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold flex items-center gap-1.5 mb-0.5 ${
                        matchScore.tailoringSignals.structuralSimilarity
                          .detected
                          ? "text-amber-800"
                          : "text-green-800"
                      }`}
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {matchScore.tailoringSignals.structuralSimilarity.detected
                        ? "Structural Mirroring Detected"
                        : "No Structural Mirroring"}
                      {matchScore.tailoringSignals.structuralSimilarity
                        .confidence > 0 && (
                        <span className="ml-auto text-[10px] font-medium">
                          {Math.round(
                            matchScore.tailoringSignals.structuralSimilarity
                              .confidence * 100,
                          )}
                          % confidence
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {matchScore.tailoringSignals.structuralSimilarity.notes ||
                        (matchScore.tailoringSignals.structuralSimilarity
                          .detected
                          ? "The CV closely follows the structure of the job description."
                          : "The CV uses a standard chronological format with no suspicious structural mirroring.")}
                    </p>
                  </div>
                )}

                {/* Exact Phrase Matches */}
                {matchScore.tailoringSignals.exactPhraseMatches &&
                matchScore.tailoringSignals.exactPhraseMatches.length > 0 ? (
                  <div className="rounded-md border border-red-200 bg-red-50/40 px-3 py-2">
                    <p className="text-xs font-semibold text-red-800 flex items-center gap-1.5 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Exact Phrase Matches (
                      {matchScore.tailoringSignals.exactPhraseMatches.length})
                    </p>
                    <ul className="space-y-1 max-h-28 overflow-y-auto">
                      {matchScore.tailoringSignals.exactPhraseMatches.map(
                        (match, i) => (
                          <li
                            key={i}
                            className="text-[10px] text-red-700 flex items-start gap-1.5"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 shrink-0" />
                            <span>&ldquo;{match.phrase}&rdquo;</span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-md border border-green-200 bg-green-50/40 px-3 py-2">
                    <p className="text-xs font-semibold text-green-800 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      No Exact Phrase Matches
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      No verbatim sequences detected from the job description.
                    </p>
                  </div>
                )}
              </div>

              {/* Keyword Overlap */}
              {matchScore.tailoringSignals.keywordOverlap &&
                matchScore.tailoringSignals.keywordOverlap.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                      Keyword Overlap
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {matchScore.tailoringSignals.keywordOverlap.map(
                        (kw, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                          >
                            {kw}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* Tailoring Indicators & AI Assessment Fallback */}
              {matchScore.tailoringSignals.tailoringIndicators &&
              matchScore.tailoringSignals.tailoringIndicators.length > 0 ? (
                <div className="mt-3">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                    Observed Indicators
                  </p>
                  <ul className="space-y-1">
                    {matchScore.tailoringSignals.tailoringIndicators.map(
                      (indicator, i) => (
                        <li
                          key={i}
                          className="text-[11px] text-foreground flex items-start gap-1.5"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1 shrink-0" />
                          {indicator}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ) : (
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 mt-4">
                  <p className="text-[11px] font-semibold text-muted-foreground mb-1">
                    AI Assessment
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {matchScore.aiProbabilityScore >= 0.7
                      ? `The AI assigned a high tailoring risk score of ${(matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV appears to closely mirror the structure, phrasing, or keyword coverage of the job description.`
                      : matchScore.aiProbabilityScore >= 0.4
                        ? `The AI assigned a medium tailoring risk score of ${(matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV shows moderate overlap with the job description — some phrasing and keyword alignment suggests deliberate tailoring.`
                        : `The AI assigned a low tailoring risk score of ${(matchScore.aiProbabilityScore * 100).toFixed(0)}%. The CV appears to be written independently with natural phrasing and authentic work history.`}
                  </p>
                </div>
              )}
            </div>
          )}
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6">
      <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
        <Link
          to="/recruitment/vacancies"
          className="text-sm font-semibold inline-flex items-center gap-2 border border-transparent hover:text-primary text-muted-foreground transition-all duration-200 h-10 px-3 rounded-md"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vacancies
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            {vacancyTitle ? `Applicants for ${vacancyTitle}` : "Applicants"}
            {isVacancyClosed && (
              <Badge
                variant="secondary"
                className="ml-3 align-middle bg-slate-100 text-slate-700"
              >
                {vacancyStatus === VacancyStatus.Expired ? "Expired" : "Closed"}
              </Badge>
            )}
          </h1>
          <div className="flex items-center gap-2">
            {!isSelectionMode && <AiCallQuotaMeter />}
            {!isSelectionMode && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Bulk Actions
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => startBulkAction("video")}>
                    <Video className="w-4 h-4 mr-2" />
                    Send Video Interviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => startBulkAction("reject")}>
                    <UserMinus className="w-4 h-4 mr-2" />
                    Reject Candidates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => startBulkAction("ai-call")}>
                    <Bot className="w-4 h-4 mr-2" />
                    Call Candidates (AI)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => setIsUploadCvDialogOpen(true)}
              disabled={isVacancyClosed}
              title={isVacancyClosed ? "This vacancy is closed" : undefined}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Candidate CV
            </Button>
          </div>
        </div>

        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search applicants..."
          filterValue={String(stageFilter)}
          onFilterChange={(value) =>
            setStageFilter(
              value === "All" ? "All" : (Number(value) as ApplicationStage),
            )
          }
          filterOptions={stageFilterOptions}
          filterPlaceholder="Status"
          count={filteredApplicationDetails.length}
          countLabel="Applicants"
          hasData={applicationDetails.length > 0}
          defaultFilterValue="All"
        />
      </div>

      {/* Bulk selection toolbar */}
      {isSelectionMode && (
        <div className="flex items-center gap-3 px-4 py-3 bg-card rounded-lg border border-border shadow-sm">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="h-4 w-4 cursor-pointer accent-primary"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.size > 0
              ? `${selectedIds.size} candidate${selectedIds.size !== 1 ? "s" : ""} selected`
              : bulkAction === "video"
                ? "Select candidates to send video interviews"
                : bulkAction === "ai-call"
                  ? `Select up to ${BULK_AI_CALL_LIMIT} candidates to call with AI`
                  : "Select candidates to reject"}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && bulkAction === "reject" && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsBulkRejectDialogOpen(true)}
              >
                <UserMinus className="w-4 h-4 mr-1.5" />
                Reject {selectedIds.size} selected
              </Button>
            )}
            {selectedIds.size > 0 && bulkAction === "video" && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsBulkVideoDialogOpen(true)}
              >
                <Video className="w-4 h-4 mr-1.5" />
                Send Video Interviews ({selectedIds.size})
              </Button>
            )}
            {selectedIds.size > 0 && bulkAction === "ai-call" && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setIsBulkAiCallDialogOpen(true)}
              >
                <Bot className="w-4 h-4 mr-1.5" />
                Call {selectedIds.size} with AI
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={exitSelectionMode}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Applicants Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : (
        <ExpandableDataTable
          columns={columns}
          data={[...filteredApplicationDetails].sort(
            (a, b) => b.matchScore.score - a.matchScore.score,
          )}
          onRowClick={(applicant) => {
            if (!isApplicationScored(applicant)) {
              toast.info(
                "This applicant is unscored — rescore them before opening the profile.",
              )
              return
            }
            if (isSelectionMode) {
              if (
                bulkAction === "video" &&
                applicationHasVideoInterview(applicant)
              ) {
                return
              }
              toggleSelection(applicant.id)
            } else {
              navigate(`/recruitment/applications/${applicant.id}`)
            }
          }}
          rowClassName={(applicant) => {
            if (isRescoringApplication(applicant.id))
              return "bg-blue-50/60 hover:!bg-blue-50/80 animate-pulse"
            if (!isApplicationScored(applicant))
              return "opacity-60 bg-muted/30 hover:!bg-muted/40"
            return ""
          }}
          actions={isSelectionMode ? undefined : getDetailedActions}
          renderExpandedRow={renderSkillBreakdown}
          emptyMessage={
            searchTerm
              ? "No applicants found matching your search."
              : "No applicants available."
          }
        />
      )}
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open)
          if (!open) setSelectedApplicant(undefined)
        }}
        onConfirm={handleDeleteApplicant}
        title="Delete Applicant"
        description={`Are you sure you want to delete "${selectedApplicant?.candidate.firstName} ${selectedApplicant?.candidate.lastName}"? This action cannot be undone.`}
        isDeleting={isDeleting}
      />
      <SendRegretDialog
        open={showSendRegretDialog}
        onOpenChange={setShowSendRegretDialog}
        onConfirm={handleSendRegretMessages}
        vacancyTitle={vacancyTitle || "this position"}
        hiredCandidateName={hiredCandidateName}
      />
      <UploadCvDialog
        open={isUploadCvDialogOpen}
        onOpenChange={setIsUploadCvDialogOpen}
        onConfirm={handleUploadCv}
        applicantName="new applicant"
      />
      <AcceptCandidateDialog
        open={isAcceptDialogOpen}
        onOpenChange={(open) => {
          setIsAcceptDialogOpen(open)
          if (!open) setSelectedApplication(null)
        }}
        onConfirm={handleAcceptCandidate}
        candidateName={
          selectedApplication
            ? `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
            : ""
        }
        candidateData={
          selectedApplication
            ? {
                firstName: selectedApplication.candidate.firstName,
                lastName: selectedApplication.candidate.lastName,
                email: selectedApplication.candidate.email,
                jobTitle:
                  vacancyTitle || selectedApplication.vacancyTitle || "",
                documentsNeeded: [],
              }
            : undefined
        }
        formId="accept-candidate-form"
        availableJobs={[]}
        isUploading={isAccepting}
      />
      <RejectCandidateDialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open)
          if (!open) setSelectedApplication(null)
        }}
        onConfirm={handleRejectCandidate}
        candidateName={
          selectedApplication
            ? `${selectedApplication.candidate.firstName} ${selectedApplication.candidate.lastName}`
            : ""
        }
        isRejecting={isRejecting}
      />
      <BulkRejectDialog
        open={isBulkRejectDialogOpen}
        onOpenChange={(open) => {
          setIsBulkRejectDialogOpen(open)
        }}
        onConfirm={handleBulkReject}
        selectedCount={selectedIds.size}
        isSubmitting={isBulkSubmitting}
      />
      <BulkCreateVideoInterviewDialog
        open={isBulkVideoDialogOpen}
        onOpenChange={(open) => {
          setIsBulkVideoDialogOpen(open)
        }}
        onConfirm={handleBulkVideoInterviewCreate}
        selectedCount={selectedIds.size}
        isSubmitting={isBulkVideoSubmitting}
        vacancyId={vacancyId}
        organizationId={currentOrganization?.id}
      />
      <BulkAiCallDialog
        open={isBulkAiCallDialogOpen}
        onOpenChange={(open) => {
          setIsBulkAiCallDialogOpen(open)
        }}
        onConfirm={handleBulkAiCallCreate}
        selectedCount={selectedIds.size}
        isSubmitting={isBulkAiCallSubmitting}
        quota={callQuota}
      />
      <CreateExpertInterviewDialog
        open={isExpertInterviewDialogOpen}
        onOpenChange={(open) => {
          setIsExpertInterviewDialogOpen(open)
          if (!open) setExpertInterviewApplication(null)
        }}
        onConfirm={handleExpertInterviewSchedule}
        isSubmitting={isSchedulingExpert}
      />
      <CreateVideoInterviewDialog
        open={isVideoInterviewDialogOpen}
        onOpenChange={(open) => {
          setIsVideoInterviewDialogOpen(open)
          if (!open) setVideoInterviewApplication(null)
        }}
        onConfirm={handleVideoInterviewCreate}
        isSubmitting={isCreatingVideoInterview}
        vacancyId={vacancyId}
        organizationId={currentOrganization?.id}
      />
      <DebugAiCallDialog
        open={isPhoneRequiredDialogOpen}
        onOpenChange={setIsPhoneRequiredDialogOpen}
      />
      <IdVerificationDialog
        open={isIdVerificationDialogOpen}
        onOpenChange={(open) => {
          setIsIdVerificationDialogOpen(open)
          if (!open) setIdVerificationApplication(null)
        }}
        candidateId={idVerificationApplication?.candidateId}
        candidateName={
          idVerificationApplication
            ? `${idVerificationApplication.candidate.firstName} ${idVerificationApplication.candidate.lastName}`
            : ""
        }
        organizationId={currentOrganization?.id}
        prefilledIdNumber={idVerificationApplication?.candidate.idNumber}
        onVerificationCompleted={(verifiedCandidateId) => {
          setApplicationDetails((prev) =>
            prev.map((app) =>
              app.candidateId === verifiedCandidateId
                ? { ...app, isIdVerificationCompleted: true }
                : app,
            ),
          )
        }}
      />
    </div>
  )
}
